import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

const column = z.array(z.string().nullable());

/**
 * Propose Supply Network Changes from the Branch Master
 *
 * Turns a branch master update into a supply network draft for a person to
 * review and publish; it never publishes. A warehouse the network doesn't
 * have yet joins its division's group, with the role its hub flag gives it
 * and a lane from its hub. A branch whose hub changed since the last branch
 * master gets the new hub's lane first, with its old lanes kept behind it.
 * Locations the branch master no longer lists are only reported.
 *
 * Takes whole frame columns, so it runs once per frame. Wire it after
 * `upsert_warehouse`, so new warehouses exist, and before
 * `upsert_warehouse_hierarchy`, whose stored hubs it compares with: once the
 * hierarchy is written, the change is no longer visible. The draft's author
 * is the user who ran the workflow (else the workflow's owner). Another
 * user's open draft fails the step, so the hierarchy waits too and a rerun
 * after that draft is finalized or deleted still sees the change.
 */
export const proposeSupplyNetworkFromBranchMasterStep = defineStepConfig({
  type: "propose_supply_network_from_branch_master",
  name: "Propose Supply Network Changes",
  description:
    "Turns a branch master update into a supply network draft for review: new warehouses join their division's group with the role from their hub flag and a lane from their hub, and a changed hub's lane goes first. Never publishes. Run it after Upsert Warehouse and before Upsert Warehouse Hierarchy.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    warehouseCodes: column.describe(
      "Each row's warehouse code (the branch master's GAINS code)",
    ),
    hubFlags: column
      .optional()
      .describe("Each row's hub or DC location flag, aligned with the codes"),
    hubCodes: column
      .optional()
      .describe("Each row's hub warehouse code, aligned with the codes"),
  }),
  outputSchema: z.object({
    status: z
      .string()
      .describe(
        "proposed, no_changes, no_published_version, or skipped_in_evaluation",
      ),
    draftVersion: z
      .number()
      .nullable()
      .describe("The draft's version number, when one was proposed"),
    addedLocations: z.number().describe("New locations in the draft"),
    hubChanges: z.number().describe("Locations whose hub's lane moved first"),
    droppedLocations: z
      .number()
      .describe("Locations the branch master no longer lists, left in place"),
    unresolved: z.number().describe("Rows the proposal couldn't use"),
  }),
  configSchema: z.object({}),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 2 },
  },
});
